import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useAssets } from 'expo-asset';
import Constants from 'expo-constants';
// Import to register background location task - must be at top level
import './services/backgroundLocation';
import * as SplashScreen from 'expo-splash-screen';
import * as Notifications from 'expo-notifications';
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Animated, StyleSheet, Text, TouchableOpacity, View, BackHandler,
  Platform,
  Linking,
} from 'react-native';
import {
  DrawerActions, NavigationContainer, getFocusedRouteNameFromRoute, useNavigation,
} from '@react-navigation/native';
import { Header, getHeaderTitle } from '@react-navigation/elements';
import { createStackNavigator } from '@react-navigation/stack';
import {
  DrawerContentScrollView,
  DrawerItem,
  createDrawerNavigator,
} from '@react-navigation/drawer';
import { ApolloProvider, useQuery } from '@apollo/client';
import {
  Avatar, Badge, DefaultTheme, IconButton, Menu, PaperProvider,
} from 'react-native-paper';
import { ToastProvider } from 'react-native-toast-notifications';
import { NativeBaseProvider } from 'native-base';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { fr, registerTranslation } from 'react-native-paper-dates';
import client from './lib/apollo-client';
import { AuthContext, AuthProvider } from './context/AuthContext';
import DashboardScreen from './screens/Dashboard/DashboardScreen';
import ManageUsersScreen from './screens/ManageUsers/ManageUsersScreen';
import LoginScreen from './screens/Authentication/LoginScreen';
import { GOOGLE_PLAY_REVIEWER_EMAIL, ROLE_NAMES, SCREEN_NAMES } from './utils/constant';
import InviteScreen from './screens/Authentication/InviteScreen';
import AcceptChildInviteScreen from './screens/Authentication/AcceptChildInviteScreen';
import AccountSelectorScreen from './screens/Authentication/AccountSelectorScreen';
import ProfileScreen from './screens/Profile/ProfileScreen';
import SignUpScreen from './screens/Authentication/SignUpScreen';
import ForgotPasswordScreen from './screens/Authentication/ForgotPasswordScreen';
import { theme } from './screens/Common.style';
import { ModalProvider } from './context/ModalContext';
import ManageAnnouncementsScreen from './screens/ManageAnnouncements/ManageAnnouncementsScreen';
import ManageEventsScreen from './screens/ManageEvents/ManageEventsScreen';
import { FindOneUserQuery } from './gql/user/query';
import EventScreen from './screens/Event/EventScreen';
import DocumentScreen from './screens/Document';
import { FindManyGlobalFiles } from './gql/file/query';
import ManageSeasonsScreen from './screens/ManageSeasons/ManageSeasonsScreen';
import ManageEquipmentScreen from './screens/ManageEquipment/ManageEquipmentScreen';
import BagDetailsScreen from './screens/ManageEquipment/BagDetailsScreen';
import AssignEquipmentToBagScreen from './screens/ManageEquipment/AssignEquipmentToBagScreen';
import ManageNotificationSettingsScreen from './screens/ManageNotificationSettings/ManageNotificationSettingsScreen';
import ManageAccessPoliciesScreen from './screens/ManageAccessPolicies/ManageAccessPoliciesScreen';

import { NotificationContext, NotificationProvider } from './context/NotificationContext';
import NotificationScreen from './screens/Notification/NotificationScreen';
import { MyUnreadNotificationsCount } from './gql/notification/query';
import StatisticsScreen from './screens/Statistics/StatisticsScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicy/PrivacyPolicyScreen';
import DeleteAccountScreen from './screens/DeleteAccount/DeleteAccountScreen';
import { canGoBack, navigationRef } from './utils/navigation';
import GoBackButton from './components/commons/GoBackButton';
import ManageTransportations from './screens/ManageTransportations/ManageTransportationsScreen';
import ManageDriverGroupsScreen from './screens/ManageDriverGroups/ManageDriverGroupsScreen';
import ManageTripsScreen from './screens/ManageTrips/ManageTripsScreen';
import TripTrackingScreen from './screens/Trip/TripTrackingScreen';
import TripPublicTrackingScreen from './screens/Trip/TripPublicTrackingScreen';
import { LocationTrackingProvider, useLocationTrackingContext } from './context/LocationTrackingContext';
import ActiveTripBanner from './components/trip/ActiveTripBanner';
import LocationDisclosureDialog from './components/location/LocationDisclosureDialog';
import { GameProvider } from './context/GameContext';
import useWhatsNew from './hooks/useWhatsNew';
import WhatsNewModal from './components/commons/WhatsNewModal';
import GameLobbyScreen from './screens/Game/GameLobbyScreen';
import GameWaitingRoomScreen from './screens/Game/GameWaitingRoomScreen';
import GameRoleRevealScreen from './screens/Game/GameRoleRevealScreen';
import GameCluePhaseScreen from './screens/Game/GameCluePhaseScreen';
import GameDiscussionScreen from './screens/Game/GameDiscussionScreen';
import GameVotingScreen from './screens/Game/GameVotingScreen';
import GameImpostorGuessScreen from './screens/Game/GameImpostorGuessScreen';
import GameResultScreen from './screens/Game/GameResultScreen';
import GameFinalScoreScreen from './screens/Game/GameFinalScoreScreen';
import ManageNasScreen from './screens/ManageNas/ManageNasScreen';
import NasScanScreen from './screens/ManageNas/NasScanScreen';
import NasBrowseScreen from './screens/NasMedia/NasBrowseScreen';

registerTranslation('fr', fr);

// Polyfill for BackHandler.removeEventListener
if (!BackHandler.removeEventListener) {
  BackHandler.removeEventListener = () => { };
}

const Stack = createStackNavigator();
const Drawer = createDrawerNavigator();

const GameStack = createStackNavigator();
function GameStackScreen() {
  return (
    <GameStack.Navigator screenOptions={{ headerShown: false }}>
      <GameStack.Screen name={SCREEN_NAMES.GAME_LOBBY} component={GameLobbyScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_WAITING_ROOM} component={GameWaitingRoomScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_ROLE_REVEAL} component={GameRoleRevealScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_CLUE_PHASE} component={GameCluePhaseScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_DISCUSSION} component={GameDiscussionScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_VOTING} component={GameVotingScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_IMPOSTOR_GUESS} component={GameImpostorGuessScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_RESULT} component={GameResultScreen} />
      <GameStack.Screen name={SCREEN_NAMES.GAME_FINAL_SCORE} component={GameFinalScoreScreen} />
    </GameStack.Navigator>
  );
}

const UserManagementStack = createStackNavigator();
function UserManagementStackScreen() {
  return (
    <UserManagementStack.Navigator screenOptions={{ headerShown: false }}>
      <UserManagementStack.Screen name={SCREEN_NAMES.USERMANAGEMENT} component={ManageUsersScreen} />
      <UserManagementStack.Screen name={SCREEN_NAMES.INVITE} component={InviteScreen} />
      <UserManagementStack.Screen name={SCREEN_NAMES.ACCEPT_CHILD_INVITE} component={AcceptChildInviteScreen} />
      <UserManagementStack.Screen name={SCREEN_NAMES.PROFILE} component={ProfileScreen} />
    </UserManagementStack.Navigator>
  );
}

const EquipmentManagementStack = createStackNavigator();
function EquipmentManagementStackScreen() {
  return (
    <EquipmentManagementStack.Navigator screenOptions={{ headerShown: false }}>
      <EquipmentManagementStack.Screen name={SCREEN_NAMES.EQUIPMENTMANAGEMENT} component={ManageEquipmentScreen} />
      <EquipmentManagementStack.Screen name={SCREEN_NAMES.BAG_DETAILS} component={BagDetailsScreen} />
      <EquipmentManagementStack.Screen name={SCREEN_NAMES.ASSIGN_EQUIPMENT_TO_BAG} component={AssignEquipmentToBagScreen} />
    </EquipmentManagementStack.Navigator>
  );
}

const EventManagementStack = createStackNavigator();
function EventManagementStackScreen() {
  return (
    <EventManagementStack.Navigator screenOptions={{ headerShown: false }}>
      <EventManagementStack.Screen name={SCREEN_NAMES.EVENTMANAGEMENT} component={ManageEventsScreen} />
      <EventManagementStack.Screen name={SCREEN_NAMES.EVENT} component={EventScreen} />
      <EventManagementStack.Screen name={SCREEN_NAMES.PROFILE} component={ProfileScreen} />
      <EventManagementStack.Screen name={SCREEN_NAMES.TRIP_TRACKING} component={TripTrackingScreen} />
    </EventManagementStack.Navigator>
  );
}

const DashboardStack = createStackNavigator();
function DashboardStackScreen() {
  return (
    <DashboardStack.Navigator screenOptions={{ headerShown: false }}>
      <DashboardStack.Screen name={SCREEN_NAMES.DASHBOARD} component={DashboardScreen} />
      <DashboardStack.Screen name={SCREEN_NAMES.EVENT} component={EventScreen} />
      <DashboardStack.Screen name={SCREEN_NAMES.PROFILE} component={ProfileScreen} />
      <DashboardStack.Screen name={SCREEN_NAMES.TRIP_TRACKING} component={TripTrackingScreen} />
    </DashboardStack.Navigator>
  );
}

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldPlaySound: false,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

const MANAGEMENT_SCREENS = [
  SCREEN_NAMES.TRIP,
  SCREEN_NAMES.USERMANAGEMENT,
  SCREEN_NAMES.ANNOUNCEMENTMANAGEMENT,
  SCREEN_NAMES.SEASONMANAGEMENT,
  SCREEN_NAMES.EVENTMANAGEMENT,
  SCREEN_NAMES.EQUIPMENTMANAGEMENT,
];
const ADMIN_SCREENS = [
  SCREEN_NAMES.MANAGE_ACCESS_POLICIES,
  SCREEN_NAMES.MANAGE_TRANSPORTATIONS,
  SCREEN_NAMES.MANAGE_DRIVER_GROUPS,
  SCREEN_NAMES.STATISTICS,
  SCREEN_NAMES.MANAGE_NAS,
  SCREEN_NAMES.NAS_SCAN,
];

function DrawerNavItem({ label, focused, onPress }) {
  return (
    <DrawerItem
      label={label}
      focused={focused}
      onPress={onPress}
      style={{ marginLeft: 8, height: 44 }}
    />
  );
}

function DrawerSectionHeader({
  label, icon, expanded, onToggle,
}) {
  return (
    <DrawerItem
      label={({ color }) => (
        <View style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1,
        }}
        >
          <Text style={{ color, fontWeight: 'bold' }}>{label}</Text>
          <IconButton icon={expanded ? 'chevron-up' : 'chevron-down'} size={18} />
        </View>
      )}
      icon={({ color, size }) => <IconButton icon={icon} size={size} iconColor={color} />}
      onPress={onToggle}
    />
  );
}

function CustomDrawerContent({ state, navigation, ...rest }) {
  const authContext = useContext(AuthContext);
  const {
    authState, switchToParent, setAuthState, logout,
  } = authContext;
  const role = authState?.role;
  const isManagement = [ROLE_NAMES.COACH, ROLE_NAMES.SUPERADMIN].includes(role);
  const isAdmin = role === ROLE_NAMES.SUPERADMIN;

  const activeRoute = state.routes[state.index]?.name;

  const [expanded, setExpanded] = useState({
    personal: true,
    management: isManagement && MANAGEMENT_SCREENS.includes(activeRoute),
    administration: isAdmin && ADMIN_SCREENS.includes(activeRoute),
  });

  const toggle = (section) => setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  return (
    <DrawerContentScrollView
      state={state}
      navigation={navigation}
      // Lets the version sit at the very bottom of the menu even when the content is short.
      contentContainerStyle={{ flexGrow: 1 }}
      {...rest}
    >
      <DrawerSectionHeader
        label="Mon espace"
        icon="account-circle"
        expanded={expanded.personal}
        onToggle={() => toggle('personal')}
      />
      {expanded.personal && (
        <View style={{ marginLeft: 20, borderLeftWidth: 2, borderLeftColor: theme.colors.primary }}>
          <DrawerNavItem
            label="Tableau de bord"
            focused={activeRoute === SCREEN_NAMES.DASHBOARD}
            onPress={() => navigation.navigate(SCREEN_NAMES.DASHBOARD)}
          />
          <DrawerNavItem
            label="Profil"
            focused={activeRoute === SCREEN_NAMES.PROFILE}
            onPress={() => navigation.navigate(SCREEN_NAMES.PROFILE)}
          />
          <DrawerNavItem
            label="Trajets"
            focused={activeRoute === SCREEN_NAMES.TRIP}
            onPress={() => navigation.navigate(SCREEN_NAMES.TRIP)}
          />
          <DrawerNavItem
            label="Jeu de l'Imposteur"
            focused={activeRoute === SCREEN_NAMES.GAME_LOBBY}
            onPress={() => navigation.navigate(SCREEN_NAMES.GAME_LOBBY)}
          />
          <DrawerNavItem
            label="Notifications"
            focused={activeRoute === SCREEN_NAMES.NOTIFICATION_SETTINGS}
            onPress={() => navigation.navigate(SCREEN_NAMES.NOTIFICATION_SETTINGS)}
          />
        </View>
      )}

      {isManagement && (
        <DrawerSectionHeader
          label="Gestion"
          icon="cog"
          expanded={expanded.management}
          onToggle={() => toggle('management')}
        />
      )}
      {isManagement && expanded.management && (
        <View style={{ marginLeft: 20, borderLeftWidth: 2, borderLeftColor: theme.colors.primary }}>
          <DrawerNavItem
            label="Utilisateurs"
            focused={activeRoute === SCREEN_NAMES.USERMANAGEMENT}
            onPress={() => navigation.navigate(SCREEN_NAMES.USERMANAGEMENT)}
          />
          <DrawerNavItem
            label="Annonces"
            focused={activeRoute === SCREEN_NAMES.ANNOUNCEMENTMANAGEMENT}
            onPress={() => navigation.navigate(SCREEN_NAMES.ANNOUNCEMENTMANAGEMENT)}
          />
          <DrawerNavItem
            label="Saisons"
            focused={activeRoute === SCREEN_NAMES.SEASONMANAGEMENT}
            onPress={() => navigation.navigate(SCREEN_NAMES.SEASONMANAGEMENT)}
          />
          <DrawerNavItem
            label="Évènements"
            focused={activeRoute === SCREEN_NAMES.EVENTMANAGEMENT}
            onPress={() => navigation.navigate(SCREEN_NAMES.EVENTMANAGEMENT)}
          />
          <DrawerNavItem
            label="Équipements"
            focused={activeRoute === SCREEN_NAMES.EQUIPMENTMANAGEMENT}
            onPress={() => navigation.navigate(SCREEN_NAMES.EQUIPMENTMANAGEMENT)}
          />
        </View>
      )}

      {isAdmin && (
        <DrawerSectionHeader
          label="Administration"
          icon="shield-crown"
          expanded={expanded.administration}
          onToggle={() => toggle('administration')}
        />
      )}
      {isAdmin && expanded.administration && (
        <View style={{ marginLeft: 20, borderLeftWidth: 2, borderLeftColor: theme.colors.primary }}>
          <DrawerNavItem
            label="Politiques d'accès"
            focused={activeRoute === SCREEN_NAMES.MANAGE_ACCESS_POLICIES}
            onPress={() => navigation.navigate(SCREEN_NAMES.MANAGE_ACCESS_POLICIES)}
          />
          <DrawerNavItem
            label="Transports"
            focused={activeRoute === SCREEN_NAMES.MANAGE_TRANSPORTATIONS}
            onPress={() => navigation.navigate(SCREEN_NAMES.MANAGE_TRANSPORTATIONS)}
          />
          <DrawerNavItem
            label="Groupes conducteurs"
            focused={activeRoute === SCREEN_NAMES.MANAGE_DRIVER_GROUPS}
            onPress={() => navigation.navigate(SCREEN_NAMES.MANAGE_DRIVER_GROUPS)}
          />
          <DrawerNavItem
            label="Statistiques"
            focused={activeRoute === SCREEN_NAMES.STATISTICS}
            onPress={() => navigation.navigate(SCREEN_NAMES.STATISTICS)}
          />
          <DrawerNavItem
            label="Configuration NAS"
            focused={activeRoute === SCREEN_NAMES.MANAGE_NAS}
            onPress={() => navigation.navigate(SCREEN_NAMES.MANAGE_NAS)}
          />
        </View>
      )}

      {authState.isActingAsChild ? (
        <DrawerItem
          label={`Revenir sur ${authState.parentSession?.username ?? 'mon compte'}`}
          icon={({ color, size }) => <IconButton icon="account-arrow-left" size={size} iconColor={color} />}
          onPress={() => switchToParent()}
        />
      ) : (
        <DrawerItem
          label="Changer de compte"
          icon={({ color, size }) => <IconButton icon="account-switch" size={size} iconColor={color} />}
          onPress={() => setAuthState((prev) => ({ ...prev, accountSelected: false }))}
        />
      )}
      <DrawerItem label="Se déconnecter" onPress={() => logout()} />
      <Text style={{
        marginTop: 'auto', padding: 16, textAlign: 'center', fontSize: 12, color: '#aaa',
      }}
      >
        {`Version ${Constants.expoConfig?.version ?? ''}`}
      </Text>
    </DrawerContentScrollView>
  );
}

function DrawerScreen() {
  const authContext = useContext(AuthContext);
  const {
    data: oneUserQuery,
    loading: oneUserQueryLoading,
    error: oneUserQueryError,
    refetch,
  } = useQuery(FindOneUserQuery, {
    variables: {
      email: authContext.authState.email,
    },
  });
  const user = oneUserQuery?.findOneUser;
  const { data } = useQuery(FindManyGlobalFiles);
  const userInitialLabel = `${authContext?.authState?.username?.split(' ')[0][0]}${authContext?.authState?.username?.split(' ')[1][0]
  }`;

  const navigation = useNavigation();
  const [menuVisible, setMenuVisible] = useState(false);
  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  const {
    shouldShow: showWhatsNew,
    dismiss: dismissWhatsNew,
    features: whatsNewFeatures,
    version: whatsNewVersion,
  } = useWhatsNew();

  return (
    <>
      <WhatsNewModal
        visible={showWhatsNew}
        onDismiss={dismissWhatsNew}
        features={whatsNewFeatures}
        version={whatsNewVersion}
      />
      <Drawer.Navigator
        initialRouteName={SCREEN_NAMES.DASHBOARD}
        drawerContent={(props) => <CustomDrawerContent {...props} />}
        screenOptions={{
          headerTitle: '',
          // Same header as the default one, plus the ongoing-trip shortcut right under it.
          header: ({ layout, options, route, navigation: headerNavigation }) => (
            <View>
              <Header {...options} layout={layout} title={getHeaderTitle(options, route.name)} />
              <ActiveTripBanner
                navigation={headerNavigation}
                hidden={getFocusedRouteNameFromRoute(route) === SCREEN_NAMES.TRIP_TRACKING}
              />
            </View>
          ),
          headerLeft: () => (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {canGoBack() ? <GoBackButton /> : null}
              <IconButton icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
            </View>
          ),
          headerRight: (props) => {
            const { data: unreadCountData } = useQuery(MyUnreadNotificationsCount, {
              skip: !authContext.authState.authenticated,
              fetchPolicy: 'network-only',
              pollInterval: 10000, // Poll every 10 seconds to get updated count
            });
            const unreadCount = unreadCountData?.myUnreadNotificationsCount || 0;

            return (
              <View {...props} style={{ flexDirection: 'row', alignItems: 'center' }}>
                {authContext.authState.isActingAsChild && (
                <IconButton
                  icon="account-switch"
                  onPress={() => authContext.setAuthState((prev) => ({ ...prev, accountSelected: false }))}
                />
                )}
                <Menu
                  visible={menuVisible}
                  onDismiss={closeMenu}
                  anchor={<IconButton icon="share-variant" onPress={openMenu} />}
                >
                  <Menu.Item
                    onPress={() => {
                      Linking.openURL('https://www.facebook.com/kcbethoncourt');
                      closeMenu();
                    }}
                    title="Facebook"
                    leadingIcon="facebook"
                  />
                  <Menu.Item
                    onPress={() => {
                      Linking.openURL('https://www.youtube.com/@Karate_Club_Bethoncourt');
                      closeMenu();
                    }}
                    title="YouTube"
                    leadingIcon="youtube"
                  />
                  <Menu.Item
                    onPress={() => {
                      Linking.openURL('https://www.instagram.com/kcbethoncourt_officiel/');
                      closeMenu();
                    }}
                    title="Instagram"
                    leadingIcon="instagram"
                  />
                  <Menu.Item
                    onPress={() => {
                      Linking.openURL('https://www.karateclubbethoncourt.fr');
                      closeMenu();
                    }}
                    title="Site officiel"
                    leadingIcon="web"
                  />
                </Menu>
                <TouchableOpacity {...props} onPress={() => navigation.navigate(SCREEN_NAMES.DOCMANAGEMENT)}>
                  <Badge
                    style={{
                      backgroundColor: theme.colors.info,
                      position: 'absolute',
                      bottom: 5,
                      left: 5,
                      zIndex: 2,
                    }}
                  >
                    {data?.findManyGlobalFiles?.length}
                  </Badge>
                  <IconButton {...props} size={30} icon="file-cabinet" />
                </TouchableOpacity>
                <TouchableOpacity {...props} onPress={() => navigation.navigate(SCREEN_NAMES.NOTIFICATION_SCREEN)}>
                  {unreadCount > 0 && (
                  <Badge
                    style={{
                      backgroundColor: theme.colors.error,
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      zIndex: 2,
                    }}
                  >
                    {unreadCount}
                  </Badge>
                  )}
                  <IconButton {...props} size={30} icon="bell" />
                </TouchableOpacity>
                <TouchableOpacity {...props} onPress={() => navigation.navigate(SCREEN_NAMES.PROFILE)}>
                  {user?.status !== 'COMPETITOR' && (
                  <Badge style={{
                    backgroundColor: theme.colors.error,
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    zIndex: 2,
                  }}
                  />
                  )}
                  <Avatar.Text {...props} size={36} label={userInitialLabel} style={{ margin: 10 }} />
                </TouchableOpacity>
              </View>
            );
          },
          drawerPosition: 'left',
        }}
      >
        <Drawer.Screen name={SCREEN_NAMES.DASHBOARD} component={DashboardStackScreen} />
        <Drawer.Screen name={SCREEN_NAMES.PROFILE} component={ProfileScreen} />
        <Drawer.Screen
          name={SCREEN_NAMES.DOCMANAGEMENT}
          component={DocumentScreen}
          options={{ drawerItemStyle: { display: 'none' } }}
        />
        {([ROLE_NAMES.COACH, ROLE_NAMES.SUPERADMIN].includes(authContext?.authState?.role)
        || authContext?.authState?.email?.toLowerCase() === GOOGLE_PLAY_REVIEWER_EMAIL) && (
        <Drawer.Screen
          name={SCREEN_NAMES.TRIP}
          component={ManageTripsScreen}
        />
        )}
        {[ROLE_NAMES.COACH, ROLE_NAMES.SUPERADMIN].includes(authContext?.authState?.role) && (
        <>
          <Drawer.Screen name={SCREEN_NAMES.USERMANAGEMENT} component={UserManagementStackScreen} />
          <Drawer.Screen
            name={SCREEN_NAMES.ANNOUNCEMENTMANAGEMENT}
            component={ManageAnnouncementsScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.SEASONMANAGEMENT}
            component={ManageSeasonsScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.EVENTMANAGEMENT}
            component={EventManagementStackScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.EQUIPMENTMANAGEMENT}
            component={EquipmentManagementStackScreen}
          />
        </>
        )}
        <Drawer.Screen
          name={SCREEN_NAMES.NOTIFICATION_SETTINGS}
          component={ManageNotificationSettingsScreen}
        />
        <Drawer.Screen
          name={SCREEN_NAMES.NOTIFICATION_SCREEN}
          component={NotificationScreen}
          options={{
            drawerItemStyle: { display: 'none' },
          }}
        />
        {authContext?.authState?.canBrowseNas && (
        <Drawer.Screen
          name={SCREEN_NAMES.NAS_BROWSE}
          component={NasBrowseScreen}
          options={{ drawerItemStyle: { display: 'none' } }}
        />
        )}
        {authContext?.authState?.role === ROLE_NAMES.SUPERADMIN && (
        <>
          <Drawer.Screen
            name={SCREEN_NAMES.MANAGE_ACCESS_POLICIES}
            component={ManageAccessPoliciesScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.MANAGE_TRANSPORTATIONS}
            component={ManageTransportations}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.MANAGE_DRIVER_GROUPS}
            component={ManageDriverGroupsScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.STATISTICS}
            component={StatisticsScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.MANAGE_NAS}
            component={ManageNasScreen}
          />
          <Drawer.Screen
            name={SCREEN_NAMES.NAS_SCAN}
            component={NasScanScreen}
            options={{ drawerItemStyle: { display: 'none' } }}
          />
        </>
        )}
        <Drawer.Screen
          name={SCREEN_NAMES.GAME_LOBBY}
          component={GameStackScreen}
          options={{
            drawerLabel: "Jeu de l'Imposteur",
            drawerIcon: ({ color, size }) => <IconButton icon="incognito" size={size} iconColor={color} />,
          }}
        />
        <Drawer.Screen
          name={SCREEN_NAMES.DELETE_ACCOUNT}
          component={DeleteAccountScreen}
          options={{
            drawerItemStyle: { display: 'none' },
          }}
        />
      </Drawer.Navigator>
    </>
  );
}

// Instruct SplashScreen not to hide yet, we want to do this manually
SplashScreen.preventAutoHideAsync().catch(() => {
  /* reloading the app might trigger some race conditions, ignore them */
});

function App() {
  const authContext = useContext(AuthContext);
  const notificationContext = useContext(NotificationContext);
  const { isDisclosureVisible, acceptDisclosure, declineDisclosure } = useLocationTrackingContext();

  useEffect(() => {
    if (notificationContext.expoPushToken) {
      authContext.setAuthState({ ...authContext.authState, notificationToken: notificationContext.expoPushToken });
    }
  }, [notificationContext.expoPushToken]);

  useEffect(() => {
    authContext.loadJWT();
  }, [authContext.loadJWT]);

  const { authenticated, accountSelected } = authContext?.authState ?? {};

  const shareToken = useMemo(() => {
    if (Platform.OS !== 'web') return null;
    return window.location.pathname.match(/^\/trip\/share\/([A-Za-z0-9]+)\/?$/)?.[1] ?? null;
  }, []);

  // Detect AcceptChildInvite URL on web — accessible regardless of auth state
  const childInviteToken = useMemo(() => {
    if (Platform.OS !== 'web') return null;
    const { pathname } = window.location;
    if (!pathname.includes('AcceptChildInvite')) return null;
    return new URLSearchParams(window.location.search).get('token');
  }, []);

  if (childInviteToken) {
    return <AcceptChildInviteScreen token={childInviteToken} />;
  }

  // Public live-tracking link (/trip/share/:token) — always shown as is, signed in or not.
  // Rendered before any navigator so the session loading can't swap it for the app.
  if (shareToken) {
    return <TripPublicTrackingScreen token={shareToken} />;
  }

  if (authenticated && !accountSelected) {
    return <AccountSelectorScreen />;
  }

  const publicWebScreens = [SCREEN_NAMES.BAG_DETAILS, SCREEN_NAMES.PRIVACY_POLICY, 'ForgotPassword'];
  const currentWebPath = Platform.OS === 'web' ? decodeURIComponent(window.location.pathname).replace(/^\//, '') : null;
  // The reset-password email links to /ForgotPassword?email=…&codeToken=… (no `linking` config, so read it here).
  const resetPasswordParams = currentWebPath === 'ForgotPassword'
    ? Object.fromEntries(new URLSearchParams(window.location.search))
    : undefined;

  return authenticated ? (
    <>
      <DrawerScreen />
      <LocationDisclosureDialog
        visible={isDisclosureVisible}
        onAccept={acceptDisclosure}
        onDecline={declineDisclosure}
      />
    </>
  ) : (
    <Stack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName={publicWebScreens.includes(currentWebPath) ? currentWebPath : 'Login'}
    >
      <Stack.Screen name="Login" component={LoginScreen} />
      <Stack.Screen name="Signup" component={SignUpScreen} />
      <Stack.Screen
        name="ForgotPassword"
        component={ForgotPasswordScreen}
        initialParams={resetPasswordParams}
      />
      <Stack.Screen name={SCREEN_NAMES.PRIVACY_POLICY} component={PrivacyPolicyScreen} />
      <Stack.Screen name={SCREEN_NAMES.BAG_DETAILS} component={BagDetailsScreen} />
    </Stack.Navigator>
  );
}

export default function () {
  const paperTheme = { ...DefaultTheme, ...theme };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <AnimatedAppLoader>
          <ApolloProvider client={client}>
            <NotificationProvider>
              <AuthProvider>
                <LocationTrackingProvider>
                  <ToastProvider>
                    <GameProvider>
                      <ModalProvider>
                        <NavigationContainer ref={navigationRef}>
                          <NativeBaseProvider>
                            <PaperProvider theme={paperTheme}>
                              <App />
                            </PaperProvider>
                          </NativeBaseProvider>
                        </NavigationContainer>
                      </ModalProvider>
                    </GameProvider>
                  </ToastProvider>
                </LocationTrackingProvider>
              </AuthProvider>
            </NotificationProvider>
          </ApolloProvider>
        </AnimatedAppLoader>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

function AnimatedAppLoader({ children }) {
  const [isSplashReady, setSplashReady] = useState(false);

  const [assets, error] = useAssets([
    require('./assets/logo.png'),
  ]);

  useEffect(() => {
    if (assets) {
      setSplashReady(true);
    }
  }, [assets]);

  if (!isSplashReady) {
    return null;
  }

  return <AnimatedSplashScreen image={assets[0]}>{children}</AnimatedSplashScreen>;
}

function AnimatedSplashScreen({ children, image }) {
  const animation = useMemo(() => new Animated.Value(1), []);
  const [isAppReady, setAppReady] = useState(false);
  const [isSplashAnimationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    if (isAppReady) {
      Animated.timing(animation, {
        toValue: 0,
        duration: 1000,
        useNativeDriver: true,
      }).start(() => setAnimationComplete(true));
    }
  }, [isAppReady]);

  const onImageLoaded = useCallback(async () => {
    try {
      await SplashScreen.hideAsync();
      // Load stuff
      await Promise.all([]);
    } catch (e) {
      // handle errors
    } finally {
      setAppReady(true);
    }
  }, []);

  return (
    <View style={{ flex: 1 }}>
      {isAppReady && children}
      {!isSplashAnimationComplete && (
        <Animated.View
          pointerEvents="none"
          style={[
            StyleSheet.absoluteFill,
            {
              backgroundColor: Constants.manifest?.splash?.backgroundColor || '#FFFFFF',
              opacity: animation,
            },
          ]}
        >
          <Animated.Image
            style={{
              width: '100%',
              height: '100%',
              resizeMode: Constants.manifest?.splash?.resizeMode || 'contain',
              transform: [
                {
                  scale: animation,
                },
              ],
            }}
            source={image}
            onLoadEnd={onImageLoaded}
            fadeDuration={0}
          />
        </Animated.View>
      )}
    </View>
  );
}
